import { z } from 'zod';

import { type AuthState, type AuthTokens } from '@/shared/store/reducers/auth/auth.types';

export const AUTH_STORAGE_KEY = 'aysar.admin.auth';

const SIGNED_OUT: AuthState = { accessToken: null, refreshToken: null };

/**
 * `.strict()`, and both fields required.
 *
 * A stored value that does not parse means one of three things: the shape
 * changed between releases, a write was interrupted, or someone edited it. All
 * three resolve to "there is no session" — the read FAILS CLOSED. The
 * alternative, salvaging whichever field survived, produces an access token with
 * no way to refresh it: the operator works for a few minutes and is then
 * signed out mid-task with no explanation.
 */
const StoredAuthSchema = z
  .object({
    accessToken: z.string().min(1),
    refreshToken: z.string().min(1),
  })
  .strict();

/**
 * Why localStorage at all: the api returns the refresh token in the response
 * body rather than a cookie, and says so — `libs/contracts/src/auth/tokens.ts`
 * names the admin SPA as a reason, so there is one code path with the native
 * app. Holding it in memory only would sign the operator out on every reload and
 * in every new tab, which for a staff portal people keep open all day is not a
 * security posture, it is an outage.
 *
 * The XSS exposure is real and is answered where P2 designed the answer: a short
 * access-token TTL plus per-request `tokenVersion` validation, so any access
 * change, password change, force-logout or ban kills the session on its NEXT
 * request rather than at the end of the token's life (N8/N22).
 */
export function readAuthFromStorage(): AuthState {
  let raw: string | null;

  try {
    raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    // Safari private mode and a blocked third-party context both throw here.
    return SIGNED_OUT;
  }

  if (raw === null) return SIGNED_OUT;

  try {
    const parsed = StoredAuthSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : SIGNED_OUT;
  } catch {
    return SIGNED_OUT;
  }
}

/** Persists a session, or removes the key entirely when there is no session. */
export function writeAuthToStorage(state: AuthState): void {
  if (state.accessToken === null || state.refreshToken === null) {
    clearAuthStorage();
    return;
  }

  const tokens: AuthTokens = {
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
  };

  try {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // A full or unavailable store must never break a sign-in that already
    // succeeded on the server; the session simply does not survive a reload.
  }
}

export function clearAuthStorage(): void {
  try {
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // Nothing to do: the read fails closed anyway.
  }
}
